import { LANGUAGE_KEY, setLanguage } from '@/redux/reducers/languageSlice';
import { normalizeLocale } from '@/utils/helpers';
import { useDispatch, useSelector } from 'react-redux';
import type { RootState } from '@/redux/store';
import { useTolgee, useTranslate } from '@tolgee/react';
import { useUserService } from '@/hooks/useUserService';
import { useCurrentUser } from '@/redux/selectors/userSelector';
import { DotIcon, LanguageIcon, SelectField } from '@/design-system';
import { GlobeIcon } from '@/design-system/components/Icons/icons/GlobeIcon';
import classNames from 'classnames';

export const LanguageSwitcher = () => {
  const tolgee = useTolgee();
  const { t } = useTranslate();
  const dispatch = useDispatch();
  const { updateLanguage } = useUserService();
  const user = useCurrentUser();

  const language = useSelector((state: RootState) => state.language.language);

  const handleSelectLanguage = (newLanguage: string) => {
    dispatch(setLanguage(newLanguage));
    void tolgee.changeLanguage(normalizeLocale(newLanguage));
    localStorage.setItem(LANGUAGE_KEY, newLanguage);
    void updateLanguage(user.id, newLanguage);
  };

  const languageMenuItems = [
    {
      value: 'de-DE',
      label: t('navigation.main.language_select.popover.german.label'),
    },
    {
      value: 'en',
      label: t('navigation.main.language_select.popover.english.label'),
    },
  ];

  return (
    <SelectField
      items={languageMenuItems}
      className={classNames('px-3!')}
      value={language}
      getValue={(i) => i.value}
      getLabel={(i) => i.label}
      renderValue={(i) => (
        <div className="flex gap-4 items-center">
          <GlobeIcon className="fill-ds-icon-4" />
          <span>{i.label}</span>
        </div>
      )}
      renderOption={(i) => (
        <div
          className={classNames(
            'flex gap-4 items-center group/menu-item transition-all duration-400 ease-in-out',
          )}
        >
          <div className="flex">
            <DotIcon
              className={classNames(
                'fill-ds-icon-2',
                'w-3 opacity-0 transition-all duration-400 ease-in-out',
                'group-hover/menu-item:w-6 group-hover/menu-item:opacity-100',
                '[.selected]:opacity-100 [.selected]:w-6',
                { selected: i.selected },
              )}
            />
            <LanguageIcon />
          </div>
          <div>{i.label}</div>
        </div>
      )}
      onChange={handleSelectLanguage}
    />
  );
};
